<script setup lang="ts">
  import * as z from 'zod';
  import { formBuilderEmailValueFieldSchema } from '../../../lib/defaultFieldValueValidation';
  import { usePropertyForm } from '../../../model/usePropertyForm';
  import type { EmailInputProperties as EmailInputPropsType } from '../../../model/form.types';
  import { FORM_FIELD_CHARACTER_LIMITS } from '../../../lib/form-field-character-limits';
  import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
  } from '@/shared/ui';

  interface Props {
    elementId?: string;
  }

  const props = defineProps<Props>();

  type FormValues = {
    name: string;
    value?: string;
    placeholder?: string;
    label?: string;
    labelPosition?: 'top' | 'left';
    required?: boolean;
  };

  usePropertyForm<FormValues, EmailInputPropsType>({
    elementId: () => props.elementId,
    validationSchema: z.object({
      name: z.string().min(1, 'O nome é obrigatório').max(FORM_FIELD_CHARACTER_LIMITS.FIELD_ID),
      value: formBuilderEmailValueFieldSchema.optional(),
      placeholder: z.string().max(FORM_FIELD_CHARACTER_LIMITS.AUXILIARY_TEXT).optional(),
      label: z.string().max(FORM_FIELD_CHARACTER_LIMITS.LABEL).optional(),
      labelPosition: z.enum(['top', 'left']).optional(),
      required: z.boolean().optional(),
    }),
    extractInitialValues: (properties: EmailInputPropsType | undefined): FormValues => ({
      name: properties?.name ?? '',
      value: properties?.value ?? '',
      placeholder: properties?.placeholder ?? '',
      label: properties?.label ?? '',
      labelPosition: properties?.labelPosition ?? 'top',
      required: properties?.required ?? false,
    }),
    mapValuesToProperties: (values: FormValues, currentProperties: EmailInputPropsType) => ({
      properties: {
        ...currentProperties,
        name: values.name || currentProperties.name,
        value: values.value || undefined,
        placeholder: values.placeholder || undefined,
        label: values.label || undefined,
        labelPosition: values.labelPosition,
        required: values.required ?? false,
      },
    }),
  });
</script>

<template>
  <div class="flex flex-col">
    <!-- ID do campo -->
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="name"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            ID do campo
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="nome_do_campo"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.FIELD_ID"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <!-- Valor -->
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="value"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Valor
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui o valor"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.VALUE"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <!-- Texto auxiliar -->
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="placeholder"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Texto auxiliar
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui o texto auxiliar"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.AUXILIARY_TEXT"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <!-- Rótulo + Posicionamento -->
    <div class="border-b border-gray-02 py-4 flex flex-col gap-[10px]">
      <FormField
        v-slot="{ componentField }"
        name="label"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Rótulo
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui"
              class="h-[42px]"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.LABEL"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField
        v-slot="{ componentField }"
        name="labelPosition"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Posicionamento
          </FormLabel>
          <Select v-bind="componentField">
            <FormControl>
              <SelectTrigger class="h-11 w-full">
                <SelectValue placeholder="Selecione a posição" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value="top">
                Acima do campo
              </SelectItem>
              <SelectItem value="left">
                À Esquerda do Campo
              </SelectItem>
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <!-- Obrigatória -->
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="required"
      >
        <FormItem class="flex items-center justify-between h-5">
          <FormLabel class="text-sm text-gray-07 font-normal cursor-pointer">
            Obrigatória
          </FormLabel>
          <FormControl>
            <Switch v-bind="componentField" />
          </FormControl>
        </FormItem>
      </FormField>
    </div>
  </div>
</template>
